import {
  formatDistanceToNowStrict,
  isFuture,
  isPast,
  parseISO,
} from "date-fns";
import { Move } from "@/types";
import React from "react";
import { Link } from "react-router-dom";
import {
  ACTIVITY_ACTION_TYPES,
  MOVE_KINDS_SHORT,
  MOVE_STATUSES,
  SALES_OUTCOMES,
  SERVICE_TYPES,
} from "@/constants";
import {
  formatCurrency,
  formatDateWithOrdinal,
  formatPhoneNumber,
  moveSalesActivities,
} from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowRight,
  Calendar,
  ClockIcon,
  LockOpenIcon,
  // FilterIcon,
  TrashIcon,
} from "lucide-react";
import FUReminder from "../Moves/components/FUReminder";
import LogCall from "../Moves/components/LogCall";
import { useGetMovesQuery, useMoveActionsMutation } from "@/store/moves/apis";
import { Loading } from "@/components/Loading";
import SetLeadLost from "../Moves/components/SetLeadLost";
import {
  faBell,
  faPhone,
} from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// const FILTERS_PATH = {
//   all: "All",
//   follow_up_due: "Follow-up Due",
//   moving_soon: "Moving Soon",
//   most_revenue: "Most Revenue",
//   repeat_customers: "Repeat Customer",
// };

const LeadTable: React.FC<{ view: string }> = ({ view }) => {
  // const [searchParams] = useSearchParams();
  // const filters = searchParams.get("filters") || "";
  const [moveActions] = useMoveActionsMutation();

  const { data, isLoading, isFetching, error, refetch } = useGetMovesQuery({
    type: "leads",
    filters: `${view || "active"}`,
    include: ["customer", "sales", "locations"],
    limit: 50,
  });

  if (isLoading || isFetching)
    return (
      <Table className="min-w-full divide-y divide-gray-200">
        <TableHeader>
          <TableRow>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Status
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Customer
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Move Date
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Details
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Recent Touchpoints
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow>
            <TableCell colSpan={6} className="py-4">
              <Loading />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  if (error) return <div>Error loading data</div>;
  if (!data) return null;

  const { data: leads, counts } = data;
  console.log(counts);

  const handleActions = async (action: string, move: Move) => {
    try {
      await moveActions({
        id: move.id,
        action,
      }).unwrap();

      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col lg:flex-row justify-start items-start gap-4">
      <Table className="min-w-full divide-y divide-gray-200">
        <TableHeader>
          <TableRow>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Status
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Customer
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Move Date
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Details
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Recent Touchpoints
            </TableHead>
            <TableHead className="bg-stone-50 hover:bg-stone-50">
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((move: Move) => (
            <TableRow
              key={move.id}
              data-move-id={move.id}
              className="hover:bg-stone-100 text-left text-sm"
            >
              <TableCell className="flex flex-col gap-0.5 w-auto whitespace-nowrap px-3 pl-0 py-3 align-top">
                <span
                  className={`font-bold ${
                    move.status === "initial"
                      ? "bg-indigo-500 text-white px-1 py-0.5"
                      : move.status === "contacting"
                      ? "bg-yellow-500 text-white px-1 py-0.5"
                      : move.status === "quoted"
                      ? "bg-amber-600 text-white px-1 py-0.5"
                      : move.status === "lost"
                      ? "bg-black text-white px-1 py-0.5"
                      : "bg-stone-500 text-white px-1 py-0.5"
                  }`}
                >
                  {MOVE_STATUSES[move.status as keyof typeof MOVE_STATUSES]}
                </span>
                <span className="text-xs text-stone-700">
                  {formatDistanceToNowStrict(parseISO(move.created_at), {
                    addSuffix: false,
                  })}{" "}
                  old
                </span>
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-3 space-y-1 align-top">
                <Link
                  className="font-semibold underline underline-offset-4 decoration-yellow-500 decoration-2"
                  to={`/move/${move.id}`}
                >
                  {move.customer_name}
                </Link>
                {move.customer?.source && (
                  <span className="text-sm flex items-center gap-1">
                    {move.customer.source}
                  </span>
                )}
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-0.5 text-sm">
                    {/* <MobileIcon className="w-3 h-3" /> */}
                    <Link
                      className="hover:underline"
                      to={`tel:${move.customer_mobile_phone}`}
                    >
                      {formatPhoneNumber(move.customer_mobile_phone)}
                    </Link>
                  </div>
                  {move.customer_alternate_phone && (
                    <div className="flex gap-1 items-center">
                      {/* <MobileIcon className="h-3 w-3" /> */}
                      <Link
                        className="hover:underline"
                        to={`tel:${move.customer_alternate_phone}`}
                      >
                        {formatPhoneNumber(move.customer_alternate_phone)}
                      </Link>
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-3 align-top flex flex-col gap-0.5 max-w-[150px]">
                <div className="flex items-start gap-1">
                  <Calendar className="h-5 w-3" />
                  <div>
                    <span className="font-medium">
                      {move.service_date
                        ? formatDateWithOrdinal(parseISO(move.service_date), {
                            showYear: false,
                          })
                        : "N/A"}
                    </span>
                    {/* {move.service_date &&
                      isFuture(parseISO(move.service_date)) && (
                        <span className="text-sm font-normal text-stone-700">
                          {" "}
                          (
                          {differenceInCalendarDays(
                            parseISO(move.service_date),
                            new Date()
                          )}{" "}
                          {differenceInCalendarDays(
                            parseISO(move.service_date),
                            new Date()
                          ) === 1
                            ? "day"
                            : "days"}
                          )
                        </span>
                      )} */}
                  </div>
                </div>
                <div className="text-wrap text-xs text-stone-700">
                  {move.same_day_move_count > 0
                    ? move.same_day_move_count
                    : "No"}{" "}
                  other move{move.same_day_move_count === 1 ? "" : "s"}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-3 align-top space-y-0.5 max-w-[200px]">
                <div>
                  {move.num_of_rooms && `${move.num_of_rooms}BR `}
                  {MOVE_KINDS_SHORT[move.kind as keyof typeof MOVE_KINDS_SHORT]}
                  {" - "}
                  {SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] ||
                    move.service ||
                    "N/A"}
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {move.origin?.city && (
                    <div className="flex items-center gap-1">
                      {/* <MapPin className="h-3 w-3" /> */}
                      <span>{move.origin.city}</span>
                    </div>
                  )}
                  {move.stops?.map((stop) => (
                    <div className="flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      <span>{stop.city}</span>
                    </div>
                  ))}
                  {move.destination?.city && (
                    <div className="flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      <span>{move.destination.city}</span>
                    </div>
                  )}
                </div>
                <div>
                  {move.revenue && move.revenue > 0 && (
                    <span className="font-semibold text-green-700">{`${formatCurrency(
                      move.revenue
                    )} est.`}</span>
                  )}
                </div>
                {/* <div>
                  {move.num_of_movers && `${move.num_of_movers} movers`}
                </div>
                <div>
                  {move.num_of_trucks && `${move.num_of_trucks} trucks`}
                </div> */}
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-3 align-top">
                <div className="flex flex-col gap-1">
                  {moveSalesActivities(move, { limit: 2 })?.map((activity) => (
                    <div key={activity.id} className="flex flex-col gap-0.5">
                      <div>
                        {
                          ACTIVITY_ACTION_TYPES[
                            activity.action as keyof typeof ACTIVITY_ACTION_TYPES
                          ]
                        }
                        {activity.type === "sales" &&
                          activity.sales_properties.outcome && (
                            <>
                              {" "}
                              -{" "}
                              {
                                SALES_OUTCOMES[
                                  activity.sales_properties[
                                    "outcome"
                                  ] as keyof typeof SALES_OUTCOMES
                                ]
                              }
                            </>
                          )}
                      </div>
                      <div className="text-xs text-stone-700">
                        {formatDistanceToNowStrict(
                          parseISO(activity.created_at),
                          { addSuffix: false }
                        )}{" "}
                        ago
                      </div>
                    </div>
                  ))}
                </div>
              </TableCell>
              <TableCell className="whitespace-nowrap px-3 py-3 align-top space-y-2">
                {move.reminders.length > 0 ? (
                  <div
                    className={`text-sm ${
                      isPast(parseISO(move.reminders[0].when.toString()))
                        ? "text-red-600"
                        : "text-amber-600"
                    } font-semibold flex items-center gap-1`}
                  >
                    <ClockIcon className="w-3 h-3" />
                    {isPast(parseISO(move.reminders[0].when.toString()))
                      ? "Follow-up past due"
                      : "Follow-up in"}{" "}
                    {isFuture(parseISO(move.reminders[0].when.toString())) &&
                      formatDistanceToNowStrict(
                        parseISO(move.reminders[0].when.toString()),
                        { addSuffix: false }
                      )}
                    {/* ago
                    {formatDateWithOrdinal(
                      parseISO(move.reminders[0].when.toString()),
                      { showYear: false }
                    )} */}
                  </div>
                ) : (
                  <span className="text-sm"></span>
                )}

                <div className="flex flex-col gap-1">
                  <LogCall
                    triggerChildren={
                      <div className="flex items-center gap-1 py-1 cursor-pointer font-medium">
                        <FontAwesomeIcon icon={faPhone} className="w-3 h-3" />
                        <span className="text-sm">Log call</span>
                      </div>
                    }
                    move={move}
                    refetchMove={refetch}
                    askForFU={false}
                  />
                  <FUReminder
                    move={move}
                    triggerChildren={
                      <div className="flex items-center gap-1 py-1 cursor-pointer font-medium">
                        <FontAwesomeIcon icon={faBell} className="w-3 h-3" />
                        <span className="text-sm">Set follow-up reminder</span>
                      </div>
                    }
                    refetchMove={refetch}
                  />
                  {move.status === "lost" || move.status === "canceled" ? (
                    <div
                      className="flex items-center space-x-2 py-1 cursor-pointer"
                      onClick={() => {
                        handleActions("reopen_move", move);
                      }}
                    >
                      <LockOpenIcon className="w-4 h-4" />
                      <span className="text-sm text-green-700">
                        Re-open lead
                      </span>
                    </div>
                  ) : (
                    <SetLeadLost
                      move={move}
                      triggerChildren={
                        <div className="flex items-center gap-1 py-1 cursor-pointer text-red-600 font-medium">
                          <TrashIcon className="w-3 h-3" />
                          <span className="text-sm">Set lead to Lost</span>
                        </div>
                      }
                      onSuccess={() => {
                        const tableRow = document.querySelector(
                          `tr[data-move-id="${move.id}"]`
                        );
                        if (tableRow) {
                          tableRow.remove();
                        }
                      }}
                    />
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default LeadTable;
